import { useMemo, useState } from 'react';
import { ISearchRequestObject, NestedKeys } from '@logz-build/typescript';
import { orderBy } from 'lodash';
import { KeyOption, matchSorter } from 'match-sorter';

interface IDataOptions<T> {
  searchKeys?: NestedKeys<T>[];
}

const filterEqualityFunciton = <T>(item: T, filter: Partial<T>) => {
  return Object.entries(filter).every(([key, value]) => {
    if (!value) return true;

    const itemValue = item[key as keyof T];

    if (Array.isArray(value)) {
      return value.includes(itemValue);
    }

    if (typeof value === 'object') {
      return filterEqualityFunciton(itemValue, value);
    }

    return itemValue === value;
  });
};

export const useCrudTableWithCompleteData = <T extends Record<string, any>>(
  sourceItems: T[],
  { searchKeys }: IDataOptions<T> = {},
) => {
  const [search, setSearch] = useState<ISearchRequestObject<T>>({});

  const data = useMemo<{ dataSource: T[]; total: number }>(() => {
    let dataSource: T[] = [...sourceItems];

    if (search.filter) {
      dataSource = dataSource.filter(item => {
        const { searchTerm, ...filter } = search.filter ?? {};

        return filterEqualityFunciton(item, filter as Partial<T>);
      });
    }

    dataSource = matchSorter(dataSource, search.filter?.searchTerm ?? '', {
      keys: (searchKeys as unknown as KeyOption<T>[]) || undefined,
      threshold: matchSorter.rankings.CONTAINS,
      sorter: !search?.sort
        ? undefined
        : rankedItems => {
            return orderBy(
              rankedItems,
              search.sort?.map(({ field }) => `item.${field}`) ?? [],
              search.sort?.map(({ descending }) => (descending ? 'desc' : 'asc')) ?? [],
            );
          },
    });

    const total = dataSource.length;

    if (search.pagination) {
      const start = (search.pagination.pageNumber - 1) * search.pagination.pageSize;
      const end = Math.min(start + search.pagination.pageSize, dataSource.length);

      dataSource = dataSource.slice(start, end);
    }

    return { dataSource, total };
  }, [search, sourceItems]);

  return {
    ...data,
    setSearch,
  };
};
